import React, { useState, useEffect, useCallback } from 'react';
import { activityLogsApi, usersApi, emailApi } from '../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { List, User, ClockClockwise, Buildings, CheckCircle, EnvelopeSimple } from '@phosphor-icons/react';

const RESOURCE_TYPES = [
  { value: 'all', label: 'Tutte le Risorse' },
  { value: 'user', label: 'Utenti' },
  { value: 'schedule', label: 'Turni' },
  { value: 'approval_request', label: 'Approvazioni' },
  { value: 'business_unit', label: 'Unità di Business' },
  { value: 'business_line', label: 'Linee di Business' },
  { value: 'team', label: 'Team' },
];

const getActionIcon = (action) => {
  if (action.includes('login') || action.includes('logout') || action.includes('register')) {
    return <User size={16} className="text-[#002FA7]" />;
  }
  if (action.includes('schedule')) {
    return <ClockClockwise size={16} className="text-[#F59E0B]" />;
  }
  if (action.includes('approval')) {
    return <CheckCircle size={16} className="text-[#22C55E]" />;
  }
  return <Buildings size={16} className="text-[#52525B]" />;
};

const getActionColor = (action) => {
  if (action.includes('delete')) return 'text-[#FF2A00]';
  if (action.includes('create')) return 'text-[#22C55E]';
  if (action.includes('update')) return 'text-[#002FA7]';
  if (action.includes('approved')) return 'text-[#22C55E]';
  if (action.includes('rejected')) return 'text-[#FF2A00]';
  return 'text-[#0A0A0A]';
};

const ActivityLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterResourceType, setFilterResourceType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [activeTab, setActiveTab] = useState('activity'); // 'activity' | 'email'
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterResourceType !== 'all') {
        params.resource_type = filterResourceType;
      }
      if (filterUser !== 'all') {
        params.user_id = filterUser;
      }

      const [logsRes, usersRes] = await Promise.all([
        activityLogsApi.getAll(params),
        usersApi.getAll(),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filterResourceType, filterUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchEmailLogs = useCallback(async () => {
    setEmailLoading(true);
    try {
      const { data } = await emailApi.getLogs(200);
      setEmailLogs(data);
    } catch (e) { console.error(e); }
    finally { setEmailLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'email' && emailLogs.length === 0) fetchEmailLogs();
  }, [activeTab, fetchEmailLogs, emailLogs.length]);

  const getUserName = (userId) => users.find((u) => u.id === userId)?.name || userId;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  };

  return (
    <div data-testid="activity-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Log Attività</h1>
          <p className="text-[#52525B] mt-1">Traccia tutte le attività e le modifiche del sistema</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 mb-4 border-b border-[#E4E4E7]">
        <button onClick={() => setActiveTab('activity')}
          className={`px-5 py-2.5 text-sm font-bold transition-colors border-b-2 ${activeTab === 'activity' ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
          data-testid="tab-activity">
          <ClockClockwise size={14} weight="bold" className="inline mr-1.5" />Attività
        </button>
        <button onClick={() => setActiveTab('email')}
          className={`px-5 py-2.5 text-sm font-bold transition-colors border-b-2 ${activeTab === 'email' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
          data-testid="tab-email">
          <EnvelopeSimple size={14} weight="bold" className="inline mr-1.5" />Email Inviate
        </button>
      </div>

      {activeTab === 'activity' ? (
        <>
        {/* Filters */}
        <div className="flex items-center gap-4 mb-4">
          <Select value={filterResourceType} onValueChange={setFilterResourceType}>
            <SelectTrigger className="w-48 rounded-none border-[#E4E4E7]" data-testid="resource-filter">
              <SelectValue placeholder="Filtra per risorsa" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {RESOURCE_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value} className="rounded-none">{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-48 rounded-none border-[#E4E4E7]" data-testid="user-filter">
              <SelectValue placeholder="Filtra per utente" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="rounded-none">Tutti gli Utenti</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id} className="rounded-none">{user.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="border border-[#E4E4E7] p-12 text-center">
          <List size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Nessun log di attività</h3>
          <p className="text-[#52525B]">Nessuna attività corrisponde ai filtri selezionati.</p>
        </div>
      ) : (
        <div className="border border-[#E4E4E7] bg-white">
          <div className="divide-y divide-[#E4E4E7]">
            {logs.map((log) => {
              const { date, time } = formatTimestamp(log.timestamp);
              return (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-4 hover:bg-[#FAFAFA] transition-colors"
                  data-testid={`log-entry-${log.id}`}
                >
                  <div className="w-10 h-10 bg-[#FAFAFA] border border-[#E4E4E7] flex items-center justify-center flex-shrink-0">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{getUserName(log.user_id)}</span>
                      <span className={`text-sm font-semibold ${getActionColor(log.action)}`}>
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-[#52525B]">
                      <span className="capitalize">{log.resource_type.replace(/_/g, ' ')}</span>
                      <span className="mx-1">•</span>
                      <span className="font-mono text-xs">{log.resource_id}</span>
                    </p>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="mt-2 p-2 bg-[#FAFAFA] border border-[#E4E4E7] text-xs font-mono">
                        {JSON.stringify(log.details, null, 2)}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-medium">{date}</p>
                    <p className="text-xs text-[#52525B]">{time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>
      ) : (
        /* Email Logs Tab */
        emailLoading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
        ) : emailLogs.length === 0 ? (
          <div className="border border-[#E4E4E7] p-12 text-center">
            <EnvelopeSimple size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <h3 className="font-heading font-bold text-lg mb-2">Nessuna email inviata</h3>
            <p className="text-[#52525B]">Le email inviate dal sistema appariranno qui.</p>
          </div>
        ) : (
          <div className="border border-[#E4E4E7] overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A] bg-white">
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase tracking-wider">Destinatario</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase tracking-wider">Oggetto</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase tracking-wider">Template</th>
                  <th className="py-3 px-4 text-center text-xs font-bold uppercase tracking-wider">Esito</th>
                  <th className="py-3 px-4 text-right text-xs font-bold uppercase tracking-wider">Data</th>
                </tr>
              </thead>
              <tbody>
                {emailLogs.map((el) => (
                  <tr key={el.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`email-log-${el.id}`}>
                    <td className="py-3 px-4 text-sm font-medium">{el.to}</td>
                    <td className="py-3 px-4 text-sm text-[#52525B] max-w-xs truncate">{el.subject}</td>
                    <td className="py-3 px-4 text-xs font-mono text-[#A1A1AA]">{el.template_key || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      {el.success ? (
                        <span className="text-[10px] font-bold bg-[#22C55E]/10 text-[#22C55E] px-2 py-0.5">OK</span>
                      ) : (
                        <span className="text-[10px] font-bold bg-[#FF2A00]/10 text-[#FF2A00] px-2 py-0.5" title={el.error || ''}>ERRORE</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-[#52525B]">
                      {el.created_at ? new Date(el.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default ActivityLogsPage;
